import React from 'react';
import { format } from 'date-fns';
import { HPEvent, Registration } from '../types';
interface ETicketProps {
  event: HPEvent;
  registration: Registration;
  state?: 'pending' | 'issued';
}
function CodeBlock({
  code
}: {
  code: string;
}) {
  const cells = Array.from({
    length: 49
  }, (_, index) => {
    const seed = code.charCodeAt(index % code.length) + index * 7;
    return seed % 3 !== 0;
  });
  return <div className="grid h-20 w-20 shrink-0 grid-cols-7 gap-[2px] rounded-sm bg-white p-1" role="img" aria-label={`Scannable code for ticket ${code}`} data-id="element-58" data-mp="2uxtfapmiusdm">
      {cells.map((filled, index) => <span key={index} className={filled ? 'bg-ink' : 'bg-white'} data-id="element-59" data-mp="2kz3tilri8ns3" data-mpi={index} />)}
    </div>;
}
export function ETicket({
  event,
  registration,
  state = 'issued'
}: ETicketProps) {
  const issued = state === 'issued';
  return <div className="overflow-hidden rounded-xl border border-ink/10 bg-ink text-white shadow-sm" data-id="element-60" data-mp="19jf9o39js4vi">
      <div className="flex items-center justify-between border-b border-white/15 px-6 py-4" data-id="element-61" data-mp="1el4hla1h42fd">
        <div className="flex items-baseline gap-2" data-id="element-62" data-mp="1h6fspl3sru5k">
          <span className="font-display text-base font-bold uppercase tracking-tight" data-id="element-63" data-mp="1d52qdkzwy93e">
            Human <span className="text-signal-light" data-id="element-64" data-mp="1r4vagxgxpr2e">Planet</span>
          </span>
          <span className="eyebrow text-white/50" data-id="element-65" data-mp="1m58x8o13watl">Events</span>
        </div>
        <span className={['rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em]', issued ? 'bg-signal text-white' : 'bg-white/10 text-white/60'].join(' ')} data-id="element-66" data-mp="1m3xwkx2yjj6p">
          {issued ? 'Valid ticket' : 'Pending review'}
        </span>
      </div>

      <div className="grid gap-6 px-6 py-6 sm:grid-cols-[1fr_auto]" data-id="element-67" data-mp="29wo2opo6j892">
        <div data-id="element-68" data-mp="24wa5k2o49ipt">
          <p className="eyebrow text-white/40" data-id="element-69" data-mp="24xtmlvhljxsn">{event.format}</p>
          <h3 className="mt-2 font-display text-2xl font-semibold leading-snug" data-id="element-70" data-mp="2ju2gehipxixo">{event.title}</h3>
          <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 text-sm" data-id="element-71" data-mp="2euzgfbhli2wk">
            <div data-id="element-72" data-mp="2lxmp36t9d11z">
              <dt className="eyebrow text-white/40" data-id="element-73" data-mp="2ly5x9vt3wb2t">Attendee</dt>
              <dd className="mt-1" data-id="element-74" data-mp="2qw7h4iycwefc">{registration.fullName}</dd>
            </div>
            <div data-id="element-75" data-mp="2qtuz65slm42a">
              <dt className="eyebrow text-white/40" data-id="element-76" data-mp="2vvkcjs3amttj">Organisation</dt>
              <dd className="mt-1" data-id="element-77" data-mp="2vt7kun7qh53a">{registration.organisation}</dd>
            </div>
            <div data-id="element-78" data-mp="30nlikw9vbob4">
              <dt className="eyebrow text-white/40" data-id="element-79" data-mp="30o3qwr9t1sze">Date</dt>
              <dd className="mt-1" data-id="element-80" data-mp="35k2am1k6umth">{format(new Date(event.date), 'EEE d MMM yyyy')}</dd>
            </div>
            <div data-id="element-81" data-mp="39le0m86z5a27">
              <dt className="eyebrow text-white/40" data-id="element-82" data-mp="39kvwyqhzftk9">Time</dt>
              <dd className="mt-1" data-id="element-83" data-mp="3elikm873cmx6">{event.timeRange}</dd>
            </div>
            <div className="col-span-2" data-id="element-84" data-mp="3jkmrgs6jm8kc">
              <dt className="eyebrow text-white/40" data-id="element-85" data-mp="3jjk2wqwp0i7l">Venue</dt>
              <dd className="mt-1" data-id="element-86" data-mp="1k80t6a3kf5wv">
                {event.venue}, {event.city}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex items-start gap-4 sm:flex-col sm:items-end" data-id="element-87" data-mp="1p7dq5no5htd6">
          <CodeBlock code={registration.ticketCode} data-id="element-88" data-mp="1hnkvq4up6q5v" />
          <div className="sm:text-right" data-id="element-89" data-mp="1hp5te3cb6p7y">
            <p className="eyebrow text-white/40" data-id="element-90" data-mp="1cwclj6ljn0rn">Ticket ref</p>
            <p className="mt-1 font-mono text-sm" data-id="element-91" data-mp="17xj8tge2zdsj">{registration.ticketCode}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-dashed border-white/25 px-6 py-4 text-xs leading-relaxed text-white/50" data-id="element-92" data-mp="12yyjwd4a4gje">
        {issued ? 'Admits one. Present this ticket, printed or on your phone, at the registration desk with photo ID. Non transferable.' : 'This is a placeholder only. Your ticket becomes valid once Human Planet approves your request, and the approved PDF is emailed to you.'}
      </div>
    </div>;
}